import React from 'react';
import { Space, Tag, Tooltip } from 'antd';

import { CategoryName, PublishedAssessment } from './matching.types';
import { budgetLabels, categoryLabels, formatMatchingTime } from './matchingPresentation';

const findingCategoryLabels = {
  ...categoryLabels,
  contract: 'Contract',
  availability: 'Availability',
  travel: 'Travel',
  evidence: 'Evidence',
};
export const AssessmentDetails = ({ assessment }: { assessment: PublishedAssessment }) => (
  <>
    <section className="matching-overview" aria-label="Assessment overview">
      <div>
        <div className="matching-muted">Overall fit</div>
        <div className="matching-score">
          {assessment.overallScore ?? '—'}
          {assessment.overallScore !== null && <small> /100</small>}
        </div>
        <Tooltip title="Share of the criteria that could be assessed. A high score can still have limited evidence.">
          <span>{assessment.assessmentCoverage}% evidence coverage</span>
        </Tooltip>
        {assessment.overallScore === null && <div className="matching-secondary">Insufficient evidence to score</div>}
      </div>
      <div>
        <h2>Assessment summary</h2>
        <p>{assessment.overallExplanation}</p>
        <Space wrap>
          <Tag>
            {assessment.cvEvidence === 'available'
              ? 'CV used in assessment'
              : assessment.cvEvidence === 'missing'
              ? 'CV missing'
              : 'CV evidence unavailable'}
          </Tag>
          <span className="matching-muted">Assessed {formatMatchingTime(assessment.evaluatedAt)}</span>
        </Space>
      </div>
    </section>
    <section className="matching-detail-section" aria-labelledby="criteria-heading">
      <h2 id="criteria-heading">Fit by criterion</h2>
      <div className="matching-categories">
        {(Object.keys(categoryLabels) as CategoryName[]).map((name) => {
          const category = assessment.categories[name];
          const rating =
            category.status === 'not_applicable'
              ? 'Not applicable'
              : category.status === 'unknown' || category.rating === null
              ? 'Unknown'
              : `${category.rating}/5`;
          return (
            <section className="matching-category" key={name} aria-label={categoryLabels[name]}>
              <div className="matching-category-heading">
                <h3>{categoryLabels[name]}</h3>
                <strong>{rating}</strong>
              </div>
              <p>{category.justification}</p>
            </section>
          );
        })}
      </div>
    </section>
    <section className="matching-detail-section" aria-labelledby="budget-heading">
      <h2 id="budget-heading">Budget</h2>
      <strong>{budgetLabels[assessment.budgetStatus]}</strong>
      <p>{assessment.budgetJustification}</p>
    </section>
    <section className="matching-detail-section" aria-labelledby="findings-heading">
      <h2 id="findings-heading">Gaps &amp; questions</h2>
      {assessment.findings.length === 0 ? (
        <p className="matching-muted">No gaps or open questions were reported.</p>
      ) : (
        <ul className="matching-findings">
          {assessment.findings.map((finding, index) => (
            <li key={index}>
              <Space wrap>
                <Tag color={finding.importance === 'major' ? 'gold' : undefined}>
                  {finding.importance === 'major' ? 'Major' : 'Minor'} {finding.kind === 'gap' ? 'gap' : 'question'}
                </Tag>
                <strong>{findingCategoryLabels[finding.category]}</strong>
              </Space>
              {finding.requirement && <div className="matching-secondary">Requirement: {finding.requirement}</div>}
              <p>{finding.explanation}</p>
            </li>
          ))}
        </ul>
      )}
    </section>
  </>
);
